/**
 * APITokensPage - manage API tokens for programmatic access
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiTokensApi, type APIToken, type APITokenCreated } from '@services/api';

export default function APITokensPage() {
  const { t } = useTranslation();

  // Tokens state
  const [tokens, setTokens] = useState<APIToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create token state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenExpiry, setNewTokenExpiry] = useState<string>('never');
  const [isCreating, setIsCreating] = useState(false);

  // Newly created token (shown once)
  const [createdToken, setCreatedToken] = useState<APITokenCreated | null>(null);
  const [copied, setCopied] = useState(false);

  // Delete confirmation
  const [tokenToDelete, setTokenToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadTokens = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiTokensApi.list();
      setTokens(response.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tokens');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const handleCreateToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTokenName.trim()) return;

    setIsCreating(true);
    setError(null);

    try {
      const expiresInDays = newTokenExpiry === 'never' ? null : parseInt(newTokenExpiry, 10);
      const token = await apiTokensApi.create({
        name: newTokenName.trim(),
        expiresInDays,
      });
      setCreatedToken(token);
      setNewTokenName('');
      setNewTokenExpiry('never');
      setShowCreateForm(false);
      await loadTokens();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create token');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyToken = async () => {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = createdToken.token;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDeleteToken = async () => {
    if (!tokenToDelete) return;

    setIsDeleting(true);
    try {
      await apiTokensApi.revoke(tokenToDelete);
      setTokens(tokens.filter((t) => t.id !== tokenToDelete));
      setTokenToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete token');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-neutral-900 dark:text-white">
            {t('settings.apiTokens', 'API Tokens')}
          </h2>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {t(
              'settings.apiTokensDescription',
              'Create tokens for programmatic API access. Use these for desktop apps, CI/CD, or integrations.'
            )}
          </p>
        </div>
        {!showCreateForm && !createdToken && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
          >
            {t('settings.createToken', 'Create Token')}
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Newly created token (show once) */}
      {createdToken && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg space-y-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                {t('settings.tokenCreated', 'Token created successfully!')}
              </p>
              <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                {t(
                  'settings.tokenCopyWarning',
                  "Copy this token now. You won't be able to see it again!"
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <code className="flex-1 p-3 bg-neutral-900 dark:bg-neutral-800 text-green-400 text-sm font-mono rounded overflow-x-auto">
              {createdToken.token}
            </code>
            <button
              onClick={handleCopyToken}
              className="px-3 py-3 bg-green-600 text-white rounded-md hover:bg-green-700"
              title="Copy to clipboard"
            >
              {copied ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              )}
            </button>
          </div>

          <button
            onClick={() => setCreatedToken(null)}
            className="text-sm text-green-700 dark:text-green-400 hover:underline"
          >
            {t('settings.tokenDismiss', "I've copied the token")}
          </button>
        </div>
      )}

      {/* Create token form */}
      {showCreateForm && (
        <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
          <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
            {t('settings.newToken', 'New API Token')}
          </h3>
          <form onSubmit={handleCreateToken} className="space-y-4">
            <div>
              <label
                htmlFor="tokenName"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.tokenName', 'Token Name')}
              </label>
              <input
                type="text"
                id="tokenName"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                placeholder="e.g., macOS Calendar Bridge"
                className="mt-1 block w-full max-w-md rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
                required
              />
              <p className="mt-1 text-xs text-neutral-500">
                {t(
                  'settings.tokenNameHelp',
                  'A descriptive name to identify this token (e.g., "CI/CD Pipeline", "Desktop App")'
                )}
              </p>
            </div>

            <div>
              <label
                htmlFor="tokenExpiry"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('settings.tokenExpiry', 'Expiration')}
              </label>
              <select
                id="tokenExpiry"
                value={newTokenExpiry}
                onChange={(e) => setNewTokenExpiry(e.target.value)}
                className="mt-1 block w-full max-w-md rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
              >
                <option value="never">{t('settings.tokenNeverExpires', 'Never expires')}</option>
                <option value="30">{t('settings.token30Days', '30 days')}</option>
                <option value="90">{t('settings.token90Days', '90 days')}</option>
                <option value="180">{t('settings.token180Days', '180 days')}</option>
                <option value="365">{t('settings.token365Days', '1 year')}</option>
              </select>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isCreating || !newTokenName.trim()}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {isCreating
                  ? t('common.creating', 'Creating...')
                  : t('settings.createToken', 'Create Token')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewTokenName('');
                  setNewTokenExpiry('never');
                }}
                className="px-4 py-2 text-neutral-600 dark:text-neutral-400 text-sm font-medium hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tokens list */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
        <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
          {t('settings.activeTokens', 'Active Tokens')}
        </h3>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tokens.length === 0 ? (
          <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-neutral-300 dark:text-neutral-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
            <p>{t('settings.noTokens', 'No API tokens yet')}</p>
            <p className="text-sm mt-1">
              {t('settings.noTokensDescription', 'Create a token to get started with API access')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {tokens.map((token) => (
              <div
                key={token.id}
                className={`flex items-center justify-between p-4 border rounded-lg ${
                  token.isExpired
                    ? 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
                    : 'border-neutral-200 dark:border-neutral-700'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      token.isExpired
                        ? 'bg-red-100 dark:bg-red-900/30'
                        : 'bg-neutral-100 dark:bg-neutral-700'
                    }`}
                  >
                    <svg
                      className={`w-5 h-5 ${
                        token.isExpired
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-neutral-500 dark:text-neutral-400'
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-neutral-900 dark:text-white">
                        {token.name}
                      </p>
                      {token.isExpired && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded">
                          {t('settings.expired', 'Expired')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
                      {token.tokenPrefix}...
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-neutral-500">
                      <span>
                        {t('settings.created', 'Created')}: {formatDate(token.createdAt)}
                      </span>
                      {token.expiresAt && (
                        <span>
                          {t('settings.expires', 'Expires')}: {formatDate(token.expiresAt)}
                        </span>
                      )}
                      {token.lastUsedAt && (
                        <span>
                          {t('settings.lastUsed', 'Last used')}: {formatDate(token.lastUsedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setTokenToDelete(token.id)}
                  className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                >
                  {t('settings.revoke', 'Revoke')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {tokenToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
              {t('settings.revokeToken', 'Revoke API Token')}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              {t(
                'settings.revokeTokenWarning',
                'Are you sure you want to revoke this token? Any applications using it will no longer be able to access the API.'
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setTokenToDelete(null)}
                className="px-4 py-2 text-neutral-600 dark:text-neutral-400 text-sm font-medium hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleDeleteToken}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting
                  ? t('common.revoking', 'Revoking...')
                  : t('settings.revokeToken', 'Revoke Token')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Usage instructions */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
        <h3 className="text-base font-medium text-neutral-900 dark:text-white mb-4">
          {t('settings.howToUse', 'How to Use')}
        </h3>
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 space-y-3">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {t(
              'settings.tokenUsageDescription',
              'Use your API token in the Authorization header of your HTTP requests:'
            )}
          </p>
          <code className="block p-3 bg-neutral-900 dark:bg-neutral-950 text-green-400 text-sm font-mono rounded overflow-x-auto">
            Authorization: Bearer nk_your_token_here
          </code>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {t(
              'settings.tokenUsageApps',
              'For desktop apps like NeoKanban Calendar Bridge, paste the token in the app preferences.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
